/**
 * 预警处置。
 *
 * 列表顺序由服务端按等级（三级 → 二级 → 一级）与发现时间给出，前端不重排业务优先级；
 * 派单需要 `alert:dispatch` 权限点 **且** 该成员在守护关系内，两者缺一即 403。
 * 403 时如实展示服务端文案，不假装派单成功。
 */
import { Fragment, useState } from 'react';
import { adminApi, ApiError } from '../lib/api.ts';
import type { AlertItem } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, PageHead } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  alertLevelFull,
  alertLevelTone,
  alertStatusLabel,
  formatConfidence,
  formatDateTime,
  scopeLabel,
} from '../lib/format.ts';

interface Toast {
  text: string;
  tone: 'ok' | 'error';
}

const LEVEL_FILTERS: { value: string; label: string }[] = [
  { value: '', label: '全部等级' },
  { value: 'danger', label: '三级（最高）' },
  { value: 'warn', label: '二级' },
  { value: 'info', label: '一级' },
];

function DispatchPanel({
  alert,
  onDone,
  onCancel,
}: {
  alert: AlertItem;
  onDone: (toast: Toast) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const result = await adminApi.dispatchAlert(alert.id, note.trim());
      onDone({
        text: `已派单：${result.alertId} · 处置人 ${result.dispatchedBy}${result.note ? ` · 备注「${result.note}」` : ''}`,
        tone: 'ok',
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `派单被拒绝（${err.status}）：${err.message}`
          : '派单请求失败，请确认后端服务可用',
      );
      setBusy(false);
    }
  };

  return (
    <div className="dispatch-panel">
      <div className="dp-t">处置派单 · {alert.title}</div>
      <div className="dp-s">
        关联成员：{alert.member.name}（{alert.member.no}）。派单需要 alert:dispatch 权限点，且该成员必须在您的
        守护关系内；服务端会同时校验两者。
      </div>
      <div className="field">
        <label className="field-label" htmlFor={`note-${alert.id}`}>
          处置备注（不超过 200 字，会随派单一并写入审计链）
        </label>
        <input
          id={`note-${alert.id}`}
          className="field-input"
          placeholder="例如：已联系成员确认症状，转合作医疗机构绿色通道"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {error && <div className="field-error">{error}</div>}
      <div className="row">
        <button type="button" className="btn primary sm" disabled={busy} onClick={() => void submit()}>
          {busy ? '正在派单…' : '确认派单'}
        </button>
        <button type="button" className="btn sm" disabled={busy} onClick={onCancel}>
          取消
        </button>
      </div>
    </div>
  );
}

export function AlertsPage() {
  const [level, setLevel] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const { data, error, loading, reload } = useLoad((signal) => adminApi.alerts(level, 50, signal));

  if (loading) return <LoadingBox text="正在加载预警列表…" />;
  if (error) return <ErrorBox message={errorText(error)} onRetry={reload} />;
  if (!data) return <ErrorBox message="预警列表为空" onRetry={reload} />;

  const items = data.items;

  const handleDone = (result: Toast): void => {
    setToast(result);
    setOpenId(null);
    reload();
    window.setTimeout(() => setToast(null), 4000);
  };

  return (
    <div>
      <PageHead
        title="预警处置"
        sub="按等级优先：三级最高，同等级按发现时间倒序（顺序由服务端给出）"
        actions={<span className="badge ghost">可见范围：{scopeLabel(data.scope)}</span>}
      />

      <Card tight>
        <div className="chip-row">
          {LEVEL_FILTERS.map((filter) => (
            <button
              key={filter.value || 'all'}
              type="button"
              className={`chip${level === filter.value ? ' on' : ''}`}
              onClick={() => {
                setLevel(filter.value);
                setOpenId(null);
              }}
            >
              {filter.label}
            </button>
          ))}
          <span className="text-tiny muted" style={{ alignSelf: 'center', marginLeft: 6 }}>
            共 {items.length} 条未解除预警
          </span>
        </div>
      </Card>

      <Card tight>
        {items.length === 0 && (
          <EmptyBox
            title="当前筛选下没有未解除预警"
            desc="预警一旦解除即从本列表移除；历史预警可在报告与审计中追溯。"
          />
        )}

        {items.length > 0 && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>等级</th>
                  <th>预警标题</th>
                  <th>成员</th>
                  <th>状态</th>
                  <th>置信度</th>
                  <th>发现时间</th>
                  <th style={{ textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <Fragment key={item.id}>
                    <tr className={openId === item.id ? 'expanded' : undefined}>
                      <td>
                        <span className={`badge ${alertLevelTone(item.level)}`}>{alertLevelFull(item.level)}</span>
                      </td>
                      <td>
                        <b>{item.title}</b>
                      </td>
                      <td>
                        {item.member.name}
                        <span className="muted mono-num"> · {item.member.no}</span>
                      </td>
                      <td>{alertStatusLabel(item.status)}</td>
                      <td className="mono-num">{formatConfidence(item.confidence)}</td>
                      <td className="mono-num">{formatDateTime(item.foundAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="link"
                          onClick={() => setOpenId(openId === item.id ? null : item.id)}
                        >
                          {openId === item.id ? '收起' : '填写备注并派单'}
                        </button>
                      </td>
                    </tr>
                    {openId === item.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: '0 10px 14px' }}>
                          <DispatchPanel alert={item} onDone={handleDone} onCancel={() => setOpenId(null)} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="note-src">
        <b>处置口径</b>
        <br />
        派单会写入审计链（member_id + 员工工号 + 目的），成员在自己的「数据调用记录」里可见；
        置信度来自平台风险模型，<b>不构成诊断</b>，最终判断由具备资质的人类专家确认。
        {items.length === 0 && ' 列表为空不能推断「平台没有风险」，只代表当前可见范围内没有未解除预警。'}
      </div>

      <div className="note-src">
        <b>权限</b>
        <br />
        派单要求 <span className="mono-num">alert:dispatch</span> 权限点，且该成员须在您的守护关系内。
        不具备该权限点的角色能看到列表，但派单会被服务端拒绝并原样提示原因 ——
        界面不会把被拒绝的派单显示成成功。
      </div>

      {toast && <div className={`toast ${toast.tone}`}>{toast.text}</div>}
    </div>
  );
}
